import { useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { ExchangeWallet, FireblocksWorkspace, TrackedWallet } from "database";
import { ChainToNameMap } from "utils/constants";
import { getChainImage } from "utils/nativeTokens";
import { capitalizeWords, slice } from "utils/string";
import WalletTableLastUpdateCell from "../components/tracking/TrackedWalletTableLastUpdateCell";
import { useNotification } from "./useNotification";
import Contact from "@components/Contact";
import { useGlobalTeam } from "@components/base-layout/header/useGlobalTeam";
import ExchangeLogo from "@components/exchanges/ExchangeLogo";
import NameEdit from "@components/NameEdit";
import { trpc } from "@utils";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import Count from "@components/wallet/Count";
import TrackedWalletTableActions from "@components/wallet/actions/TrackedWalletActions";
import ExchangeWalletsActionCell from "@components/wallet/actions/ExchangesActions";
import FireblocksActionCell from "@components/wallet/actions/FireblocksActions";
import { useCopyToClipboard } from "./useCopyToClipboard";
import StatusIndicator from "@components/wallet/StatusIndicator";

export type Wallet = TrackedWallet | ExchangeWallet | FireblocksWorkspace;
export type Wallets = Wallet[] | undefined;

type SingleWallet = TrackedWallet & {
    _count: {
        Transaction: number;
    };
};

type Page = "tracking" | "exchanges" | "fireblocks";

const columnHelper = createColumnHelper<
    SingleWallet & ExchangeWallet & FireblocksWorkspace
>();

const calculateWalletProgress = (wallet: SingleWallet) => {
    const inQ = wallet.txsInQueue ?? 0;
    const expected = wallet._count.Transaction + inQ;
    return 100 - Math.round((inQ / expected) * 100);
};

const getAddressColumn = (
    page: Page,
    copyToClipboard: (content: string) => void,
) => {
    if (page === "tracking") {
        return columnHelper.accessor("address", {
            id: "address",
            header: "Address",
            cell: ({ getValue }) => {
                const address = getValue();

                return (
                    <div className="flex items-center gap-2">
                        <AccountBalanceWalletRoundedIcon fontSize="small" />
                        <div className="relative">
                            {slice(address, 6)}
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copyToClipboard(address);
                                }}
                                className="absolute right-0 top-0 z-30 translate-x-5 text-text-icon opacity-0 transition-opacity duration-200 ease-in-out hover:text-text-icon-hover group-hover:opacity-100"
                                title="Copy address"
                            >
                                <ContentCopyRoundedIcon
                                    sx={{ fontSize: "16px" }}
                                />
                            </button>
                        </div>
                    </div>
                );
            },
        });
    }

    return undefined;
};

const getChainColumn = (page: Page) => {
    if (page === "tracking") {
        return columnHelper.accessor("chain", {
            header: () => {
                return <div className="hidden xl:block">Network</div>;
            },
            cell: ({ getValue }) => {
                return (
                    <div className="flex items-center justify-center gap-2 xl:justify-start">
                        <img
                            src={getChainImage(getValue())}
                            alt={getValue()}
                            className="size-4"
                        />
                        <span className="hidden xl:flex">
                            {ChainToNameMap[getValue()]}
                        </span>
                    </div>
                );
            },
        });
    }

    if (page === "exchanges") {
        return columnHelper.accessor("platform", {
            header: () => {
                return <div className="hidden xl:block">Platform</div>;
            },
            cell: ({ getValue }) => {
                return (
                    <div className="inline-flex h-full items-center gap-2">
                        <ExchangeLogo
                            platform={getValue()}
                            size={16}
                            onError={(e) => {
                                e.currentTarget.src =
                                    "/images/unknown_token.png";
                                e.currentTarget.title = "Unknown";
                            }}
                        />
                        {capitalizeWords(getValue(), "_", " ", {
                            us: "US",
                        })}
                    </div>
                );
            },
        });
    }

    return undefined;
};

const getNameColumn = (
    page: Page,
    isLoading: boolean,
    editName: (arg0: { id: string; name: string }) => Promise<void>,
) => {
    if (page === "tracking") {
        return columnHelper.display({
            header: "Name",
            cell: ({ row }) => {
                return (
                    <Contact
                        walletAddress={row.original.address}
                        chain={row.original.chain}
                    />
                );
            },
        });
    }

    return columnHelper.accessor("name", {
        header: "Name",
        cell: ({ row, getValue }) => {
            return (
                <NameEdit
                    onSave={(name) => editName({ id: row.original.id, name })}
                    placeholder="Enter a name"
                    defaultValue={getValue()}
                    isLoading={isLoading}
                />
            );
        },
    });
};

const getCount = (page: Page) => {
    return columnHelper.accessor("_count.Transaction", {
        header: "Count",
        cell: ({ getValue, row }) => {
            // Case 1: Tracking page
            if (page === "tracking") {
                return (
                    <Count count={getValue()}>
                        <TrackedWalletTableActions
                            id={row.original.id}
                            status={row.original.status}
                            chain={row.original.chain}
                            address={row.original.address}
                        />
                    </Count>
                );
            }

            // Case 2: Exchanges page
            if (page === "exchanges") {
                return (
                    <Count count={getValue()}>
                        <ExchangeWalletsActionCell
                            id={row.original.id}
                            status={row.original.status}
                            name={row.original.name}
                        />
                    </Count>
                );
            }

            // Case 3: Fireblocks page
            if (page === "fireblocks") {
                return (
                    <Count count={getValue()}>
                        <FireblocksActionCell
                            id={row.original.id}
                            status={row.original.status}
                            name={row.original.name}
                        />
                    </Count>
                );
            }

            return <span className="flex items-center gap-1" />;
        },
    });
};

const useTableColumns = ({
    page,
    wallets,
}: {
    page: Page;
    wallets: Wallets;
}) => {
    const { selectedTeam } = useGlobalTeam();
    const notify = useNotification();
    const { copyToClipboard } = useCopyToClipboard();
    const [previousWalletsSequence, setPreviousWalletsSequence] = useState<
        string[]
    >([]);

    const utils = trpc.useContext();
    const { mutateAsync: editExchangeName, isLoading: isEditingExchangeName } =
        trpc.exchangeWallet.editName.useMutation({
            onSuccess: async () => {
                await utils.exchangeWallet.list.invalidate();
            },
            onError: (err) => {
                notify.error(
                    `
                There was an error updating the exchange wallet. Error: ${err.message}`,
                );
            },
        });
    const {
        mutateAsync: editFireblocksName,
        isLoading: isEditingFireblocksName,
    } = trpc.fireblocks.editName.useMutation({
        onSuccess: async () => {
            await utils.fireblocks.list.invalidate();
        },
        onError: (err) => {
            notify.error(
                `
                There was an error updating the fireblocks workspace. Error: ${err.message}`,
            );
        },
    });

    const columns = useMemo(() => {
        return [
            getAddressColumn(page, copyToClipboard),
            getNameColumn(
                page,
                page === "exchanges"
                    ? isEditingExchangeName
                    : isEditingFireblocksName,
                page === "exchanges" ? editExchangeName : editFireblocksName,
            ),
            getChainColumn(page),
            columnHelper.accessor("timestamps.updatedAt", {
                header: "Last Updated",
                cell: ({ getValue }) => {
                    return (
                        <WalletTableLastUpdateCell
                            value={getValue()}
                            timezone={selectedTeam!.timezone}
                        />
                    );
                },
            }),
            columnHelper.accessor("status.code", {
                header: () => {
                    return <div className="hidden xl:block">Status</div>;
                },
                cell: ({ row }) => {
                    return (
                        <StatusIndicator
                            statusCode={row.original.status.code}
                            progress={calculateWalletProgress(row.original)}
                            txInQueue={row.original.txsInQueue}
                            updateOrCreation={
                                row.original.timestamps.createdAt.toUTCString() ===
                                row.original.timestamps.updatedAt.toUTCString()
                                    ? "creation"
                                    : "update"
                            }
                        />
                    );
                },
            }),
            getCount(page),
        ].filter(Boolean);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        page,
        isEditingExchangeName,
        isEditingFireblocksName,
        editExchangeName,
        editFireblocksName,
        selectedTeam,
    ]);

    useEffect(() => {
        const newDataSequence = wallets?.map((wallet) => wallet.id);
        if (
            newDataSequence &&
            JSON.stringify(newDataSequence) !==
                JSON.stringify(previousWalletsSequence)
        ) {
            // Data has changed, update state
            setPreviousWalletsSequence(newDataSequence);
        }
    }, [wallets, previousWalletsSequence]);

    // Return the columns directly
    return columns;
};

export default useTableColumns;
